import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Stack,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  MenuItem,
  LinearProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import { FolderOpen, CheckCircle, WarningAmber, ErrorOutline, AccountBalanceWallet, Refresh } from '@mui/icons-material';
import projectAnalyticsService from '../../services/projectAnalyticsService';
import {
  PageHeader,
  StatCard,
  FilterBar,
  FilterSpacer,
  TableSurface,
  headerCellSx,
  bodyRowSx,
  StatusDot,
  TypePill,
  ErrorState,
  TableEmptyState,
  StatusTone,
} from '../../components/common/pageKit';
import { useChartTokens } from '../../dashboard/chartTokens';
import { formatIndianNumber } from '../../utils/currency';

// ---- pure formatters ----
const isNum = (v: any) => v !== null && v !== undefined && !isNaN(Number(v));
const fmtMoney = (v: any) => (isNum(v) ? formatIndianNumber(Number(v)) : '—');
const fmtPct = (v: any) => (isNum(v) ? `${Math.round(Number(v))}%` : '—');
const fmtDate = (v: any) => {
  if (!v) return '—';
  const d = new Date(v);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const HEALTH_TONE: Record<string, StatusTone> = {
  ON_TRACK: 'success',
  WATCH: 'info',
  AT_RISK: 'warning',
  CRITICAL: 'error',
};
const HEALTH_LABEL: Record<string, string> = {
  ON_TRACK: 'On track',
  WATCH: 'Watch',
  AT_RISK: 'At risk',
  CRITICAL: 'Critical',
};
const HEALTH_OPTIONS = ['ON_TRACK', 'WATCH', 'AT_RISK', 'CRITICAL'];
const STATUS_OPTIONS = ['PLANNING', 'IN_PROGRESS', 'ON_HOLD', 'COMPLETED'];

const GROUP_OPTIONS: Array<{ key: GroupKey; label: string }> = [
  { key: 'none', label: 'No grouping' },
  { key: 'status', label: 'Status' },
  { key: 'health', label: 'Health' },
  { key: 'manager', label: 'Manager' },
  { key: 'customer', label: 'Customer' },
  { key: 'type', label: 'Type' },
];

type GroupKey = 'none' | 'status' | 'health' | 'manager' | 'customer' | 'type';

const groupValue = (p: any, key: GroupKey): string => {
  switch (key) {
    case 'status':
      return p.status || 'Unassigned';
    case 'health':
      return HEALTH_LABEL[p.health?.band] || p.health?.band || 'Unassigned';
    case 'manager':
      return p.managerName || 'Unassigned';
    case 'customer':
      return p.customerName || 'Unassigned';
    case 'type':
      return p.projectType || 'Unassigned';
    default:
      return '';
  }
};

const ProjectPortfolioPage: React.FC = () => {
  const navigate = useNavigate();
  const T = useChartTokens();

  const [data, setData] = useState<{ projects: any[]; count: number; groups: any } | null>(null);
  const [baseGroups, setBaseGroups] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [status, setStatus] = useState('');
  const [health, setHealth] = useState('');
  const [projectType, setProjectType] = useState('');
  const [groupBy, setGroupBy] = useState<GroupKey>('none');

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    const params: Record<string, any> = {};
    if (status) params.status = status;
    if (health) params.health = health;
    if (projectType) params.projectType = projectType;

    projectAnalyticsService
      .portfolio(params)
      .then((res) => {
        setData(res);
        // Capture the unfiltered option universe only once, from the first
        // load that carries no filters — narrower filtered results must not
        // shrink the dropdowns out from under the user.
        if (!baseGroups && !status && !health && !projectType) setBaseGroups(res.groups);
      })
      .catch((e: any) => setError(e?.message || 'Failed to load the project portfolio.'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, health, projectType]);

  useEffect(() => {
    load();
  }, [load]);

  const projects = data?.projects || [];
  const groups = data?.groups || {};

  const typeOptions = useMemo(() => Object.keys(baseGroups?.byType || groups.byType || {}).filter(Boolean), [baseGroups, groups]);

  const stats = useMemo(
    () => [
      { key: 'total', title: 'Projects', value: data?.count ?? projects.length, icon: <FolderOpen />, color: T.categorical[0] },
      { key: 'onTrack', title: 'On Track', value: groups.byHealth?.ON_TRACK ?? 0, icon: <CheckCircle />, color: T.status.good },
      { key: 'atRisk', title: 'At Risk', value: groups.byHealth?.AT_RISK ?? 0, icon: <WarningAmber />, color: T.status.serious },
      { key: 'critical', title: 'Critical', value: groups.byHealth?.CRITICAL ?? 0, icon: <ErrorOutline />, color: T.status.critical },
      {
        key: 'budget',
        title: 'Total Budget',
        value: fmtMoney(projects.reduce((s, p) => s + (Number(p.budget) || 0), 0)),
        icon: <AccountBalanceWallet />,
        color: T.categorical[3],
      },
      {
        key: 'contract',
        title: 'Total Contract Value',
        value: fmtMoney(projects.reduce((s, p) => s + (Number(p.contractValue) || 0), 0)),
        icon: <AccountBalanceWallet />,
        color: T.categorical[2],
      },
    ],
    [data, projects, groups, T]
  );

  const groupedRows = useMemo(() => {
    if (groupBy === 'none') return [{ label: null as string | null, rows: projects }];
    const map = new Map<string, any[]>();
    projects.forEach((p) => {
      const k = groupValue(p, groupBy);
      if (!map.has(k)) map.set(k, []);
      map.get(k)!.push(p);
    });
    return Array.from(map.entries())
      .sort((a, b) => b[1].length - a[1].length)
      .map(([label, rows]) => ({ label, rows }));
  }, [projects, groupBy]);

  const goWorkspace = (id: any) => navigate(`/projects/${id}/workspace`);

  return (
    <Box>
      <PageHeader
        title="Project Portfolio"
        subtitle="Every active project — contract, cost, schedule, and health in one table"
        actions={
          <Tooltip title="Refresh">
            <IconButton onClick={load} size="small">
              <Refresh fontSize="small" />
            </IconButton>
          </Tooltip>
        }
      />

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {error && !data ? (
        <ErrorState message={error} onRetry={load} />
      ) : (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {stats.map((s) => (
              <Grid key={s.key} size={{ xs: 6, sm: 4, md: 2 }}>
                <StatCard title={s.title} value={s.value} icon={s.icon} color={s.color} />
              </Grid>
            ))}
          </Grid>

          <FilterBar>
            <TextField select size="small" label="Status" value={status} onChange={(e) => setStatus(e.target.value)} sx={{ minWidth: 160 }}>
              <MenuItem value="">All Statuses</MenuItem>
              {STATUS_OPTIONS.map((s) => (
                <MenuItem key={s} value={s}>
                  {s.replace('_', ' ')}
                </MenuItem>
              ))}
            </TextField>
            <TextField select size="small" label="Health" value={health} onChange={(e) => setHealth(e.target.value)} sx={{ minWidth: 160 }}>
              <MenuItem value="">All Health</MenuItem>
              {HEALTH_OPTIONS.map((h) => (
                <MenuItem key={h} value={h}>
                  {HEALTH_LABEL[h]}
                </MenuItem>
              ))}
            </TextField>
            <TextField select size="small" label="Type" value={projectType} onChange={(e) => setProjectType(e.target.value)} sx={{ minWidth: 160 }}>
              <MenuItem value="">All Types</MenuItem>
              {typeOptions.map((t) => (
                <MenuItem key={t} value={t}>
                  {t}
                </MenuItem>
              ))}
            </TextField>
            <FilterSpacer />
            <TextField
              select
              size="small"
              label="Group by"
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value as GroupKey)}
              sx={{ minWidth: 160 }}
            >
              {GROUP_OPTIONS.map((g) => (
                <MenuItem key={g.key} value={g.key}>
                  {g.label}
                </MenuItem>
              ))}
            </TextField>
          </FilterBar>

          <TableSurface>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={headerCellSx}>Project</TableCell>
                  <TableCell sx={headerCellSx}>Customer</TableCell>
                  <TableCell sx={headerCellSx}>Manager</TableCell>
                  <TableCell sx={headerCellSx}>Type</TableCell>
                  <TableCell sx={headerCellSx} align="right">Contract</TableCell>
                  <TableCell sx={headerCellSx} align="right">Budget</TableCell>
                  <TableCell sx={headerCellSx} align="right">Actual</TableCell>
                  <TableCell sx={headerCellSx} align="right">Variance</TableCell>
                  <TableCell sx={headerCellSx}>Progress</TableCell>
                  <TableCell sx={headerCellSx}>Health</TableCell>
                  <TableCell sx={headerCellSx}>Schedule</TableCell>
                  <TableCell sx={headerCellSx} align="right">Billed / Collected</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {groupedRows.map((group) => (
                  <React.Fragment key={group.label ?? '__flat'}>
                    {group.label !== null && (
                      <TableRow>
                        <TableCell colSpan={12} sx={{ bgcolor: 'action.hover', py: 0.75, borderBottom: '1px solid', borderColor: 'divider' }}>
                          <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                            {group.label} · {group.rows.length}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                    {group.rows.map((p: any) => {
                      const band = p.health?.band || 'ON_TRACK';
                      const variance = Number(p.variance);
                      const overBudget = isNum(variance) && variance < 0;
                      return (
                        <TableRow key={p.id} hover sx={{ ...bodyRowSx, cursor: 'pointer' }} onClick={() => goWorkspace(p.id)}>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {p.name || '—'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                              {p.code || ''}
                            </Typography>
                          </TableCell>
                          <TableCell>{p.customerName || '—'}</TableCell>
                          <TableCell>{p.managerName || '—'}</TableCell>
                          <TableCell>
                            {p.projectType ? <TypePill label={p.projectType} tint={T.categorical[1]} /> : '—'}
                          </TableCell>
                          <TableCell align="right">{fmtMoney(p.contractValue)}</TableCell>
                          <TableCell align="right">{fmtMoney(p.budget)}</TableCell>
                          <TableCell align="right">{fmtMoney(p.actualCost)}</TableCell>
                          <TableCell align="right">
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600, color: !isNum(variance) ? 'text.primary' : overBudget ? 'error.main' : 'success.main' }}
                            >
                              {fmtMoney(variance)}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ minWidth: 130 }}>
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <Box sx={{ flex: 1 }}>
                                <LinearProgress
                                  variant="determinate"
                                  value={Math.min(Math.max(Number(p.progressPercent) || 0, 0), 100)}
                                  sx={{ height: 6, borderRadius: 3 }}
                                />
                              </Box>
                              <Typography variant="caption" sx={{ fontWeight: 600, minWidth: 30 }}>
                                {fmtPct(p.progressPercent)}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <StatusDot label={HEALTH_LABEL[band] || band} tone={HEALTH_TONE[band] || 'neutral'} />
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" color="text.secondary">
                              {fmtDate(p.startDate)} → {fmtDate(p.plannedEndDate || p.endDate)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2">{fmtMoney(p.billed)}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {fmtMoney(p.collected)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
            {!loading && !projects.length && (
              <TableEmptyState title="No projects match these filters" hint="Try clearing a filter to widen the results." />
            )}
          </TableSurface>
        </>
      )}
    </Box>
  );
};

export default ProjectPortfolioPage;
